import { getSupportedLanguages } from './messages';

export interface LanguageMetadata {
  code: string;
  name: string;
  nativeName: string;
  flag: string;
  countryCode: string;
}

// 메모리 캐시 — 모든 LocaleLoader 인스턴스가 공유 (main.ts 는 언어 변경마다 new LocaleLoader() 를 만듦)
const translationCache = new Map<string, any>();
// 동일 module/locale 에 대한 진행 중 fetch 공유 (동시 호출 N회 → 네트워크 요청 1회)
const inFlightRequests = new Map<string, Promise<any>>();

/**
 * public 폴더에서 다국어 파일을 동적으로 로드 (캐싱 포함)
 */
export class LocaleLoader {
  private cache = translationCache;
  private cacheTimeout = 1 * 60 * 1000; // 1분으로 단축
  private forceRefresh = false; // 강제 갱신 플래그

  /**
   * 언어 코드를 파일명 형식으로 정규화
   * en-GB -> en, en-US -> en (영어 파일은 en.json으로 통일)
   */
  private normalizeLocaleForFile(locale: string): string {
    const lowerLocale = locale.toLowerCase();
    // 영어 변형들은 모두 en.json 사용
    if (lowerLocale === 'en-gb' || lowerLocale === 'en-us' || lowerLocale === 'en-sg') {
      return 'en';
    }
    return locale;
  }

  async loadLocale(module: string, locale: string, forceRefresh = false): Promise<any> {
    // 파일명용 언어 코드 정규화 (en-GB -> en)
    const fileLocale = this.normalizeLocaleForFile(locale);
    const cacheKey = `${module}-${locale}`;

    // 강제 갱신이 아니고 캐시가 있으면 사용 (캐시 엔트리 = { data, _lastModified }; 번역 데이터만 돌려줌)
    if (!forceRefresh && this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey).data;
    }

    // 이미 같은 파일을 받는 중이면 그 Promise 를 재사용 (중복 요청 방지)
    if (!forceRefresh && inFlightRequests.has(cacheKey)) {
      return inFlightRequests.get(cacheKey);
    }

    const request: Promise<any> = this.fetchLocale(module, fileLocale, locale, cacheKey, forceRefresh)
      .finally(() => {
        // 내 요청이 아직 대표 요청일 때만 제거 (강제 갱신이 뒤에 등록된 경우 그 엔트리를 지우지 않음)
        if (inFlightRequests.get(cacheKey) === request) inFlightRequests.delete(cacheKey);
      });
    inFlightRequests.set(cacheKey, request);
    return request;
  }

  private async fetchLocale(module: string, fileLocale: string, locale: string, cacheKey: string, forceRefresh: boolean): Promise<any> {
    try {
      // public/locales/modules/ 경로로 요청 (캐시 버스팅)
      const timestamp = forceRefresh ? Date.now() : 0;
      const url = `/locales/modules/${module}/${fileLocale}.json${timestamp ? `?t=${timestamp}` : ''}`;
      const response = await fetch(url);
      
      // Content-Type 확인 (응답 본문을 읽기 전에 확인)
      const contentType = response.headers.get('content-type') || '';
      
      // HTML이 반환된 경우 (404 페이지 등) - response.ok가 true여도 HTML일 수 있음
      if (contentType.includes('text/html')) {
        console.debug(`⚠️  Translation file not found (HTML returned): ${module}/${locale}`);
        return null;
      }
      
      if (response.ok) {
        // JSON이 아닌 경우 에러
        if (contentType && !contentType.includes('application/json')) {
          console.debug(`⚠️  Unexpected content type for ${module}/${locale}: ${contentType}`);
          return null;
        }
        
        const data = await response.json();
        const lastModified = response.headers.get('last-modified');
        
        // 타임스탬프와 함께 캐시에 저장 (데이터와 메타를 분리 → _lastModified 가 번역 메시지에 섞이지 않음)
        const cacheData = {
          data,
          _lastModified: lastModified ? new Date(lastModified).getTime() : Date.now()
        };
        
        this.cache.set(cacheKey, cacheData);
        
        // 캐시 만료 설정
        setTimeout(() => this.cache.delete(cacheKey), this.cacheTimeout);
        
        console.log(`✅ Loaded: ${module}/${locale}${forceRefresh ? ' (forced refresh)' : ''}`);
        return data;
      } else if (response.status === 404) {
        // 404 에러는 조용히 처리 (파일이 없는 것은 정상)
        console.debug(`⚠️  Translation file not found: ${module}/${locale} (404)`);
        return null;
      } else {
        console.debug(`⚠️  Failed to load: ${module}/${locale} (${response.status})`);
        return null;
      }
    } catch (error) {
      // JSON 파싱 에러인 경우 더 명확한 메시지 (조용히 처리)
      if (error instanceof SyntaxError && error.message.includes('JSON')) {
        console.debug(`⚠️  Translation file not found or invalid JSON: ${module}/${locale}`);
        return null;
      }
      // 기타 에러도 조용히 처리 (네트워크 에러 등)
      const errorMessage = error instanceof Error ? error.message : String(error);
      console.debug(`⚠️  Error loading locale: ${module}/${locale}`, errorMessage);
      return null;
    }
  }

  /**
   * 여러 모듈의 번역을 한번에 병렬로 로드 (성능 최적화)
   */
  async loadMultipleModules(modules: string[], locale: string): Promise<Record<string, any>> {
    const results: Record<string, any> = {};
    
    // 모든 모듈을 병렬로 로드
    const promises = modules.map(async (module) => {
      try {
        const data = await this.loadLocale(module, locale);
        if (data) {
          results[module] = data;
        }
      } catch (error) {
        console.debug(`Failed to load ${module}/${locale}:`, error);
      }
      return null; // Promise.all에서 undefined 방지
    });

    await Promise.all(promises);
    return results;
  }

  /**
   * 특정 모듈/언어 강제 갱신
   */
  async refreshModule(module: string, locale: string): Promise<any> {
    console.log(`🔄 Force refreshing: ${module}/${locale}`);
    return await this.loadLocale(module, locale, true);
  }

  /**
   * 파일 경로에서 모듈 이름 추출
   */
  private getModuleNameFromPath(path: string): string | null {
    const match = path.match(/modules\/([^/]+)\//);
    return match ? match[1] : null;
  }

  /**
   * 파일 시스템에서 모듈 목록 자동 감지
   */
  private detectModules(): string[] {
    const modules = new Set<string>();
    
    try {
      // import.meta.glob을 사용하여 모듈 목록 자동 감지
      // dynamicLoader.ts는 utils/locales/ 폴더에 있으므로 modules는 같은 레벨에 있음
      const moduleTranslations = import.meta.glob('./modules/*/*.json', { eager: true });
      Object.keys(moduleTranslations).forEach(path => {
        const moduleName = this.getModuleNameFromPath(path);
        if (moduleName) {
          modules.add(moduleName);
        }
      });
    } catch (error) {
      console.warn('Failed to detect modules automatically, using fallback:', error);
      // 폴백: 기본 모듈 목록 사용
      return ['auth', 'client', 'common', 'dashboard', 'menu', 'signUp'];
    }
    
    const moduleList = Array.from(modules).sort();
    console.log('📁 Detected modules for refresh:', moduleList);
    return moduleList;
  }

  /**
   * 모든 캐시 강제 갱신
   */
  async refreshAll(): Promise<void> {
    console.log('🔄 Force refreshing all translations...');
    const modules = this.detectModules();
    // messages.ts에서 지원 언어 목록 가져오기
    const supportedLanguages = getSupportedLanguages();
    const locales = supportedLanguages.map(lang => lang.code);
    
    for (const module of modules) {
      for (const locale of locales) {
        await this.loadLocale(module, locale, true);
      }
    }
    console.log('✅ All translations refreshed');
  }

  /**
   * 파일 변경 감지 (1시간마다 정시 체크)
   * - 매시간 정시에 체크 (00분)
   * - 사용자 액션 기반 갱신
   * - 수동 갱신 버튼 제공
   */
  private lastCheckTime = 0;
  private checkInterval = 60 * 60 * 1000; // 1시간
  private watcherInterval: number | null = null;
  private isChecking = false; // 중복 체크 방지
  private focusCheckCount = 0; // 포커스 체크 횟수
  private maxFocusChecksPerHour = 3; // 시간당 최대 포커스 체크 횟수
  private languageCheckCount = 0; // 언어 변경 체크 횟수
  private maxLanguageChecksPerHour = 5; // 시간당 최대 언어 체크 횟수
  private lastHourReset = Date.now(); // 시간당 카운터 리셋
  private sseConnection: EventSource | null = null; // SSE 연결
  private sseClientId: string | null = null; // SSE 클라이언트 ID

  startFileWatcher(): void {
    console.log('👀 Starting SSE-based translation watcher...');
    
    // 1. SSE 연결 시작
    this.startSSEConnection();

    // 2. 페이지 포커스 시 체크 (최적화됨)
    document.addEventListener('visibilitychange', () => {
      if (!document.hidden) {
        this.checkForUpdatesWithLimit('focus');
      }
    });

    // 3. 언어 변경 시 체크 (최적화됨)
    window.addEventListener('language-changed', () => {
      this.checkForUpdatesWithLimit('language');
    });

    // 4. 1시간마다 정시에 체크 (백업용)
    this.scheduleHourlyCheck();
  }

  private scheduleHourlyCheck(): void {
    const now = new Date();
    const nextHour = new Date(now);
    nextHour.setHours(now.getHours() + 1, 0, 0, 0); // 다음 정시
    
    const timeUntilNextHour = nextHour.getTime() - now.getTime();
    
    console.log(`⏰ Next check scheduled at: ${nextHour.toLocaleTimeString()}`);
    
    // 다음 정시까지 대기
    setTimeout(() => {
      this.checkForUpdates();
      
      // 이후 1시간마다 반복
      this.watcherInterval = window.setInterval(() => {
        this.checkForUpdates();
      }, this.checkInterval);
      
    }, timeUntilNextHour);
  }

  /**
   * SSE 연결 시작
   */
  private startSSEConnection(): void {
    try {
      this.sseClientId = `client_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const sseUrl = `/api/admin/translation/stream?clientId=${this.sseClientId}`;
      
      this.sseConnection = new EventSource(sseUrl);
      
      this.sseConnection.onopen = () => {
        console.log('✅ SSE connection opened:', this.sseClientId);
      };
      
      this.sseConnection.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          this.handleSSEMessage(data);
        } catch (error) {
          console.warn('Failed to parse SSE message:', error);
        }
      };
      
      this.sseConnection.onerror = (error) => {
        console.warn('SSE connection error:', error);
        // 연결 재시도
        setTimeout(() => {
          if (this.sseConnection?.readyState === EventSource.CLOSED) {
            console.log('🔄 Reconnecting SSE...');
            this.startSSEConnection();
          }
        }, 5000);
      };
      
    } catch (error) {
      console.error('Failed to start SSE connection:', error);
    }
  }

  /**
   * SSE 메시지 처리
   */
  private handleSSEMessage(data: any): void {
    console.log('📨 SSE message received:', data);
    
    switch (data.type) {
      case 'connection':
        console.log('✅ SSE connected:', data.clientId);
        break;
        
      case 'ping':
        // 연결 유지 확인
        break;
        
      case 'translation-refresh':
      case 'translation-refresh-all':
      case 'translation-file-updated':
        console.log('🔄 Translation update received via SSE:', data);
        // 즉시 번역 갱신
        this.refreshAll();
        break;
        
      default:
        console.log('Unknown SSE message type:', data.type);
    }
  }

  /**
   * SSE 연결 중지
   */
  private stopSSEConnection(): void {
    if (this.sseConnection) {
      this.sseConnection.close();
      this.sseConnection = null;
      this.sseClientId = null;
      console.log('⏹️ SSE connection closed');
    }
  }

  stopFileWatcher(): void {
    // SSE 연결 중지
    this.stopSSEConnection();
    
    // 기존 폴링 중지
    if (this.watcherInterval) {
      window.clearInterval(this.watcherInterval);
      this.watcherInterval = null;
      console.log('⏹️ File watcher stopped');
    }
  }

  /**
   * 서버에서 번역 갱신 확인 (페이지 이동 시)
   */
  async checkServerUpdates(): Promise<void> {
    try {
      const response = await fetch('/api/admin/translation/status', {
        method: 'GET',
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          console.log('🔄 Server translation status checked:', data.timestamp);
          // 서버 상태 확인 후 클라이언트 캐시 갱신
          await this.checkForUpdates();
        }
      }
    } catch (error) {
      console.warn('Failed to check server translation updates:', error);
    }
  }

  /**
   * 서버에 클라이언트 등록 (일괄 갱신 수신용)
   */
  async registerForUpdates(): Promise<string | null> {
    try {
      const clientId = `client_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      const response = await fetch('/api/admin/translation/register-client', {
        method: 'GET',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('✅ Registered for translation updates:', data.clientId);
        return data.clientId;
      }
    } catch (error) {
      console.warn('Failed to register for translation updates:', error);
    }
    return null;
  }

  /**
   * 서버에서 일괄 갱신 요청 (관리자 API 사용)
   */
  async requestGlobalRefresh(module?: string, locale?: string): Promise<boolean> {
    try {
      const response = await fetch('/api/admin/translation/refresh-all', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          module,
          locale,
          force: true
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('🌍 Admin: Global translation refresh requested:', data);
        
        // 로컬 캐시도 갱신
        await this.refreshAll();
        
        return true;
      } else {
        console.warn('Admin API access denied or failed:', response.status);
      }
    } catch (error) {
      console.warn('Failed to request global translation refresh:', error);
    }
    return false;
  }

  /**
   * 제한된 갱신 체크 (성능 최적화)
   */
  async checkForUpdatesWithLimit(type: 'focus' | 'language'): Promise<void> {
    const now = Date.now();
    
    // 시간당 카운터 리셋 (1시간마다)
    if (now - this.lastHourReset > 60 * 60 * 1000) {
      this.focusCheckCount = 0;
      this.languageCheckCount = 0;
      this.lastHourReset = now;
    }
    
    // 타입별 제한 확인
    if (type === 'focus' && this.focusCheckCount >= this.maxFocusChecksPerHour) {
      console.log('🚫 Focus check limit reached (3/hour), skipping...');
      return;
    }
    
    if (type === 'language' && this.languageCheckCount >= this.maxLanguageChecksPerHour) {
      console.log('🚫 Language check limit reached (5/hour), skipping...');
      return;
    }
    
    // 중복 체크 방지
    if (this.isChecking) {
      console.log('⏳ Check already in progress, skipping...');
      return;
    }
    
    // 카운터 증가
    if (type === 'focus') this.focusCheckCount++;
    if (type === 'language') this.languageCheckCount++;
    
    // 실제 체크 실행
    await this.checkForUpdates();
  }

  async checkForUpdates(): Promise<void> {
    const now = Date.now();
    
    // 너무 빈번한 체크 방지 (최소 1분 간격)
    if (now - this.lastCheckTime < 60000) {
      return;
    }
    
    this.isChecking = true;
    this.lastCheckTime = now;
    
    try {
      // HEAD 요청으로 마지막 수정 시간만 확인 (가벼움)
      const response = await fetch('/locales/modules/auth/en.json', { 
        method: 'HEAD',
        cache: 'no-cache'
      });
      
      if (response.ok) {
        const lastModified = response.headers.get('last-modified');
        const cached = this.cache.get('auth-en');
        
        // 캐시된 데이터와 비교하여 변경 여부 확인
        if (cached && lastModified) {
          const cachedTime = cached._lastModified;
          if (!cachedTime || new Date(lastModified).getTime() > cachedTime) {
            console.log('🔄 Translation files updated, refreshing...');
            await this.refreshAll();
          }
        }
      }
    } catch (error) {
      console.warn('File watcher check error:', error);
    } finally {
      this.isChecking = false; // 체크 완료 후 플래그 리셋
    }
  }

  /**
   * 캐시 초기화
   */
  clearCache(): void {
    this.cache.clear();
    console.log('🧹 Translation cache cleared');
  }

  /**
   * 캐시 상태 확인
   */
  getCacheInfo(): { size: number; keys: string[] } {
    return {
      size: this.cache.size,
      keys: Array.from(this.cache.keys())
    };
  }

  /**
   * 체크 상태 및 제한 정보 확인
   */
  getCheckStatus(): {
    isChecking: boolean;
    focusChecks: number;
    languageChecks: number;
    lastCheckTime: number;
    nextResetTime: number;
    sseConnected: boolean;
    sseClientId: string | null;
  } {
    return {
      isChecking: this.isChecking,
      focusChecks: this.focusCheckCount,
      languageChecks: this.languageCheckCount,
      lastCheckTime: this.lastCheckTime,
      nextResetTime: this.lastHourReset + 60 * 60 * 1000,
      sseConnected: this.sseConnection?.readyState === EventSource.OPEN,
      sseClientId: this.sseClientId
    };
  }
}

// 싱글톤 인스턴스
export const localeLoader = new LocaleLoader();

// 개발자 도구용 글로벌 함수 (개발 환경에서만)
if (import.meta.env.DEV) {
  (window as any).refreshTranslations = () => localeLoader.refreshAll();
  (window as any).clearTranslationCache = () => localeLoader.clearCache();
  (window as any).getTranslationCache = () => localeLoader.getCacheInfo();
  (window as any).getCheckStatus = () => localeLoader.getCheckStatus();
  (window as any).checkServerUpdates = () => localeLoader.checkServerUpdates();
  (window as any).registerForUpdates = () => localeLoader.registerForUpdates();
  (window as any).requestGlobalRefresh = (module?: string, locale?: string) => localeLoader.requestGlobalRefresh(module, locale);
  (window as any).startTranslationWatcher = () => localeLoader.startFileWatcher();
  (window as any).stopTranslationWatcher = () => localeLoader.stopFileWatcher();
  (window as any).checkForUpdates = () => localeLoader.checkForUpdates();
  
  console.log('🛠️ Translation debug functions available:');
  console.log('  - refreshTranslations() - 개별 브라우저만 갱신');
  console.log('  - requestGlobalRefresh() - 🌍 모든 사용자에게 일괄 갱신');
  console.log('  - registerForUpdates() - 서버 알림 등록');
  console.log('  - clearTranslationCache() - 캐시 클리어');
  console.log('  - getTranslationCache() - 캐시 상태 확인');
  console.log('  - getCheckStatus() - 체크 상태 및 SSE 연결 정보');
  console.log('  - checkServerUpdates() - 서버에서 번역 상태 확인');
  console.log('  - startTranslationWatcher() - SSE 기반 실시간 감지 시작');
  console.log('  - stopTranslationWatcher() - SSE 연결 및 파일 감지 중지');
  console.log('  - checkForUpdates() - 수동 업데이트 체크');
}
